import React, { useState } from 'react';
import { toast } from 'sonner';
import { Crown, Check, Loader2, Sparkles, Clock, ArrowUp, Coins } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { useAuth } from '../auth';
import { useLang } from '../i18n';
import api from '../api';

// Premium sales are toggled by an admin (DB-backed). We fetch the live value;
// this constant is only the initial fallback until the fetch resolves. While
// disabled, all Premium entry points open this dialog in a "coming soon" state.
const PREMIUM_ENABLED_DEFAULT = false;

// Ordering of plans, used to offer upgrades to existing subscribers.
const PLAN_RANK = { premium_monthly: 1, premium_yearly: 2, premium_lifetime: 3 };

const PLANS = [
  { key: 'premium_monthly', price: '$4.99', per: { de: '/ Monat', en: '/ month' }, points: { de: '200 Punkte / Monat', en: '200 points / month' } },
  { key: 'premium_yearly', price: '$39.99', per: { de: '/ Jahr', en: '/ year' }, badge: { de: '−33%', en: '−33%' }, points: { de: '2.500 Punkte / Jahr', en: '2,500 points / year' } },
  { key: 'premium_lifetime', price: '$89.99', per: { de: 'einmalig', en: 'one-time' }, points: { de: 'Unbegrenzte Punkte', en: 'Unlimited points' } },
];

// One-time point top-up packs (shown when the user runs low / at 0).
const POINT_PACKS = [
  { key: 'points_100', points: '100', price: '$2.99' },
  { key: 'points_500', points: '500', price: '$9.99' },
  { key: 'points_1500', points: '1.500', price: '$24.99' },
];

export const PricingDialog = () => {
  const { user, isPaid, entitlement, promptLogin, pricingOpen, openPricing, closePricing } = useAuth();
  const { lang } = useLang();
  const en = lang === 'en';
  const t = (de, eng) => (en ? eng : de);
  const [busy, setBusy] = useState('');
  const [PREMIUM_ENABLED, setPremiumEnabled] = useState(PREMIUM_ENABLED_DEFAULT);
  const onClose = closePricing;

  // Load the live "sales enabled" flag (admin toggle) when the dialog opens.
  React.useEffect(() => {
    if (!pricingOpen) return;
    let ok = true;
    api.getPremiumConfig()
      .then((r) => { if (ok) setPremiumEnabled(!!r.data?.sales_enabled); })
      .catch(() => { /* keep default */ });
    return () => { ok = false; };
  }, [pricingOpen]);

  // The plan the user currently owns (if any) -> shown as "Aktuell" / greyed.
  const currentPlanKey = (isPaid && entitlement?.plan && ['monthly', 'yearly', 'lifetime'].includes(entitlement.plan))
    ? `premium_${entitlement.plan}`
    : null;
  const currentRank = currentPlanKey ? (PLAN_RANK[currentPlanKey] || 0) : 0;

  // Deep link: /?premium=1 (used by header links on SSG + SPA) opens the dialog.
  React.useEffect(() => {
    try {
      if (new URLSearchParams(window.location.search).get('premium') === '1') openPricing();
    } catch (e) { console.error('Pricing: non-critical error:', e); }
  }, [openPricing]);

  const perks = [
    t('Punkte für Generierungen & Exporte', 'Points for generations & exports'),
    t('Werbefrei', 'Ad-free'),
    t('Etiketten ohne Grenzen', 'Labels without limits'),
  ];

  const buy = async (key) => {
    // Existing subscribers may upgrade to a higher plan even while sales are
    // "coming soon"; everyone else is blocked until Premium goes live.
    const isUpgrade = currentRank > 0 && (PLAN_RANK[key] || 0) > currentRank;
    if (!PREMIUM_ENABLED && !isUpgrade) return;
    if (!user) { onClose(); promptLogin(t('Bitte melde dich an, um Premium zu kaufen.', 'Please sign in to purchase Premium.')); return; }
    setBusy(key);
    try {
      const res = await api.createCheckout({ lookup_key: key, origin_url: window.location.origin });
      if (res.data?.checkout_url) window.location.href = res.data.checkout_url;
      else throw new Error('no url');
    } catch (e) {
      toast.error(t('Checkout konnte nicht gestartet werden.', 'Could not start checkout.'));
      setBusy('');
    }
  };

  return (
    <Dialog open={pricingOpen} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-lg max-h-[90dvh] overflow-y-auto" data-testid="pricing-dialog">
        <DialogHeader className="items-center text-center">
          <div className="mx-auto mb-1 flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
            <Crown className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="font-heading text-xl">Barcode Studio Premium</DialogTitle>
          <DialogDescription>
            {currentPlanKey
              ? t('Du bist Premium. Wechsle jederzeit auf einen höheren Plan.',
                   'You are Premium. Upgrade to a higher plan anytime.')
              : PREMIUM_ENABLED
                ? t('Wähle deinen Plan – jederzeit im Stripe-Testmodus.', 'Choose your plan — Stripe test mode.')
                : t('Premium-Abos sind bald verfügbar. Wirf schon mal einen Blick auf die kommenden Vorteile.',
                     'Premium plans are coming soon. Here is a sneak peek at what is on the way.')}
          </DialogDescription>
        </DialogHeader>

        {!PREMIUM_ENABLED && !currentPlanKey && (
          <div className="mx-auto flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3.5 py-1.5 text-sm font-semibold text-primary" data-testid="pricing-coming-soon">
            <Clock className="h-4 w-4" />
            {t('Bald verfügbar', 'Coming soon')}
          </div>
        )}

        <ul className="mx-auto mb-1 grid gap-1.5 text-sm">
          {perks.map((p) => (
            <li key={p} className="flex items-center gap-2 text-muted-foreground">
              <Check className="h-4 w-4 text-primary" />{p}
            </li>
          ))}
        </ul>

        <div className="grid gap-3 sm:grid-cols-3">
          {PLANS.map((pl) => {
            const isCurrent = pl.key === currentPlanKey;
            const rank = PLAN_RANK[pl.key] || 0;
            const isUpgrade = currentRank > 0 && rank > currentRank;
            const isDowngrade = currentRank > 0 && rank < currentRank;
            const greyed = isCurrent || isDowngrade || (!isUpgrade && !PREMIUM_ENABLED);
            return (
            <div key={pl.key} className={`relative flex flex-col rounded-xl border p-4 text-center ${isCurrent ? 'border-primary/50 bg-muted/50' : (isUpgrade ? 'border-primary/60' : '')} ${greyed ? 'opacity-60' : ''}`} data-testid={`plan-card-${pl.key}`}>
              {isCurrent ? (
                <Badge className="absolute -top-2 right-3 bg-primary hover:bg-primary" data-testid={`plan-current-badge-${pl.key}`}>{t('Aktuell', 'Current')}</Badge>
              ) : isUpgrade ? (
                <Badge className="absolute -top-2 right-3 bg-primary hover:bg-primary" data-testid={`plan-upgrade-badge-${pl.key}`}>{t('Upgrade', 'Upgrade')}</Badge>
              ) : pl.badge && (
                <Badge className="absolute -top-2 right-3 bg-primary hover:bg-primary">{en ? pl.badge.en : pl.badge.de}</Badge>
              )}
              <div className="font-heading text-2xl font-bold text-foreground">{pl.price}</div>
              <div className="text-xs text-muted-foreground">{en ? pl.per.en : pl.per.de}</div>
              <div className="mb-3 mt-1 text-xs font-semibold text-primary" data-testid={`plan-points-${pl.key}`}>{en ? pl.points.en : pl.points.de}</div>
              {isCurrent ? (
                <Button variant="secondary" className="mt-auto w-full gap-1.5" disabled data-testid={`buy-${pl.key}`}>
                  <Check className="h-4 w-4" />{t('Aktuell', 'Current')}
                </Button>
              ) : isUpgrade ? (
                <Button className="mt-auto w-full gap-1.5" disabled={!!busy} onClick={() => buy(pl.key)} data-testid={`buy-${pl.key}`}>
                  {busy === pl.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowUp className="h-4 w-4" />}
                  {t('Upgrade', 'Upgrade')}
                </Button>
              ) : isDowngrade ? (
                <Button variant="secondary" className="mt-auto w-full gap-1.5" disabled data-testid={`buy-${pl.key}`}>
                  {t('Nicht wählbar', 'Not available')}
                </Button>
              ) : PREMIUM_ENABLED ? (
                <Button className="mt-auto w-full gap-1.5" disabled={!!busy} onClick={() => buy(pl.key)} data-testid={`buy-${pl.key}`}>
                  {busy === pl.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  {t('Wählen', 'Choose')}
                </Button>
              ) : (
                <Button variant="secondary" className="mt-auto w-full gap-1.5" disabled data-testid={`buy-${pl.key}`}>
                  <Clock className="h-4 w-4" />{t('Bald verfügbar', 'Coming soon')}
                </Button>
              )}
            </div>
            );
          })}
        </div>

        {/* Point top-up packs */}
        <div className="mt-1 border-t pt-3" data-testid="points-packs">
          <p className="mb-2 text-center text-sm font-semibold text-foreground">
            {t('Punkte nachkaufen', 'Buy more points')}
          </p>
          <div className="grid gap-2 sm:grid-cols-3">
            {POINT_PACKS.map((pk) => (
              <div key={pk.key} className={`flex flex-col items-center rounded-lg border p-3 text-center ${PREMIUM_ENABLED ? '' : 'opacity-60'}`} data-testid={`pack-card-${pk.key}`}>
                <div className="font-heading text-base font-bold text-foreground">{pk.points}</div>
                <div className="mb-2 text-[11px] text-muted-foreground">{t('Punkte', 'points')} · {pk.price}</div>
                {PREMIUM_ENABLED ? (
                  <Button size="sm" variant="outline" className="w-full gap-1" disabled={!!busy} onClick={() => buy(pk.key)} data-testid={`buy-${pk.key}`}>
                    {busy === pk.key ? <Loader2 className="h-4 w-4 animate-spin" /> : <Coins className="h-4 w-4" />}
                    {t('Kaufen', 'Buy')}
                  </Button>
                ) : (
                  <Button size="sm" variant="secondary" className="w-full gap-1" disabled data-testid={`buy-${pk.key}`}>
                    <Clock className="h-3.5 w-3.5" />{t('Bald', 'Soon')}
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
        {PREMIUM_ENABLED && (
          <p className="text-center text-xs text-muted-foreground">
            {t('Testkarte: 4242 4242 4242 4242, beliebiges künftiges Datum & CVC.', 'Test card: 4242 4242 4242 4242, any future date & CVC.')}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Handles /payment/success and /payment/cancel (SPA sub-routes).
export const PaymentReturn = () => {
  const { refresh } = useAuth();
  const { lang } = useLang();
  const en = lang === 'en';
  const [msg, setMsg] = useState(en ? 'Verifying payment…' : 'Zahlung wird geprüft…');

  React.useEffect(() => {
    const path = window.location.pathname;
    if (path.includes('/payment/cancel')) {
      toast.info(en ? 'Payment cancelled.' : 'Zahlung abgebrochen.');
      window.location.replace('/');
      return;
    }
    const sid = new URLSearchParams(window.location.search).get('session_id');
    if (!sid) { window.location.replace('/'); return; }
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const res = await api.paymentStatus(sid);
        if (res.data?.payment_status === 'paid') {
          try { await refresh(); } catch (e) { console.error('Pricing: non-critical error:', e); }
          toast.success(en ? 'Premium activated — thank you!' : 'Premium aktiviert – vielen Dank!');
          window.location.replace('/');
          return;
        }
      } catch (e) { console.error('Pricing: non-critical error:', e); }
      if (tries >= 10) { setMsg(en ? 'Still processing… you can close this page.' : 'Wird noch verarbeitet… du kannst die Seite schließen.'); window.location.replace('/'); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, [refresh, en]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4" data-testid="payment-return">
      <div className="flex flex-col items-center gap-3 text-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">{msg}</p>
      </div>
    </div>
  );
};
